import { useState, useRef, useEffect, useCallback } from "react";

export interface SearchItem {
  icon: string;
  label: string;
  desc: string;
  href: string;
  section?: string;
  color?: string;
}

interface DashboardSearchProps {
  items: SearchItem[];
  placeholder?: string;
  highlightColor?: string;
  fullWidth?: boolean;
}

function highlight(text: string, query: string, color: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color, fontWeight: 700 }}>{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

const INTER: React.CSSProperties = { fontFamily: "'Inter', system-ui, sans-serif" };
const MONO: React.CSSProperties  = { fontFamily: "'JetBrains Mono', 'Fira Code', monospace" };

export default function DashboardSearch({ items, placeholder = "Search features, pages, tools…", highlightColor = "#f59e0b", fullWidth = false }: DashboardSearchProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = query.trim();

  const results = trimmed
    ? (() => {
        const q = trimmed.toLowerCase();
        const labelMatches: SearchItem[] = [];
        const descMatches: SearchItem[] = [];
        for (const item of items) {
          if (item.label.toLowerCase().includes(q)) {
            labelMatches.push(item);
          } else if (item.desc.toLowerCase().includes(q)) {
            descMatches.push(item);
          }
        }
        return [...labelMatches, ...descMatches];
      })()
    : [];

  function clearSearch() {
    if (inputRef.current) inputRef.current.value = "";
    setQuery("");
    setOpen(false);
  }

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      if (inputRef.current) inputRef.current.value = "";
      setQuery("");
      setOpen(false);
      inputRef.current?.blur();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className="ds-root"
      style={{ position: "relative", width: "100%" }}
    >
      {!fullWidth && (
        <style>{`
          @media (min-width: 768px) {
            .ds-root { max-width: 384px; }
          }
        `}</style>
      )}
      {/* Input */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#18181b",
        border: `1px solid ${open && trimmed ? "#3f3f46" : "#27272a"}`,
        borderRadius: 10,
        padding: "0 12px",
        transition: "border-color 0.15s",
      }}>
        <span style={{ fontSize: 14, color: "#71717a", flexShrink: 0, userSelect: "none" }}>🔍</span>
        <input
          ref={inputRef}
          type="search"
          data-testid="dashboard-search-input"
          placeholder={placeholder}
          autoComplete="off"
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => { if (trimmed) setOpen(true); }}
          style={{
            ...INTER,
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#fff",
            fontSize: 13,
            padding: "10px 0",
            minWidth: 0,
          }}
        />
        {query && (
          <button
            onClick={() => { clearSearch(); inputRef.current?.focus(); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#71717a", fontSize: 14, padding: "0 2px", lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && trimmed && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 6px)",
          left: 0,
          right: 0,
          zIndex: 9999,
          background: "#27272a",
          border: "1px solid #3f3f46",
          borderRadius: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}>
          {results.length === 0 ? (
            <div style={{ ...INTER, fontSize: 12, color: "#71717a", padding: "14px 16px", textAlign: "center" }}>
              No results for "{trimmed}"
            </div>
          ) : (
            <div style={{ maxHeight: 340, overflowY: "auto" }}>
              {results.map((item, i) => (
                <a
                  key={`${item.href}-${i}`}
                  href={item.href}
                  data-testid={`dashboard-search-result-${i}`}
                  onClick={() => { setOpen(false); setQuery(""); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    textDecoration: "none",
                    borderBottom: i < results.length - 1 ? "1px solid #3f3f46" : "none",
                    transition: "background 0.1s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = "#3f3f46"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; }}
                >
                  <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...INTER, fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                      {highlight(item.label, trimmed, highlightColor)}
                    </div>
                    <div style={{
                      ...INTER,
                      fontSize: 11,
                      color: "#a1a1aa",
                      lineHeight: 1.4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginTop: 1,
                    }}>
                      {item.desc}
                    </div>
                  </div>
                  {item.section && (
                    <span style={{
                      ...MONO,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      color: item.color ?? highlightColor,
                      background: `${item.color ?? highlightColor}18`,
                      border: `1px solid ${item.color ?? highlightColor}30`,
                      borderRadius: 4,
                      padding: "2px 7px",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}>
                      {item.section.toUpperCase()}
                    </span>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
